import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { mcpModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

export default function useMCPConnections() {
  const { t } = useTranslation();
  const { showToast } = useToaster();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const tr = useCallback((key, fallback, values = {}) => t(`mcp.${key}`, { defaultValue: fallback, ...values }), [t]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const hasOrgAdminPermission = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.ORG_ADMIN);

  const reportError = useCallback((message) => {
    const value = message || tr('request_failed', 'Unable to complete the request. Please try again.');
    setError(value);
    showToastRef.current(value, 'e');
  }, [tr]);

  useEffect(() => {
    if (!hasOrgAdminPermission) {
      reportError(tr('org_admin_required', 'Only an organization administrator can connect or manage AI applications.'));
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    HttpServices.Get({
      url: mcpModuleURL.connections,
      signal: controller.signal,
      successCallback: (result) => {
        if (!controller.signal.aborted) {
          setData(result);
          setLoading(false);
        }
      },
      failureCallback: (message) => {
        if (!controller.signal.aborted) {
          reportError(message);
          setLoading(false);
        }
      },
    });
    return () => controller.abort();
  }, [hasOrgAdminPermission, reload, reportError, tr]);

  return {
    data,
    error,
    loading,
    refresh: () => setReload((value) => value + 1),
    reportError,
    showToast,
    tr,
  };
}
